import { RiskNotice } from "../RiskNotice";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { WarningIcon, LightbulbIcon, CheckCircleIcon } from "@phosphor-icons/react";
import { customerApi, hasCustomerToken } from "@/lib/customerApi";
import StoreTopbar from "../StoreTopbar";
import HubFooter from "../HubFooter";
import { ChartBanner, Shot } from "./GuideMocks";

function buildSteps() {
  return [
    { h: "Create your account and choose a plan", path: "botgenomix.com  >  Create a free account  >  Choose a plan",
      do: ["Click Create a free account, type your first name, email and a password (8+ characters).",
           "Tick the box to say you've read the terms and risk disclosure, then click Create account.",
           "On your dashboard pick a plan (Single bot, Five bots or Everything), tick the box underneath and click Continue to secure checkout.",
           "Pay on the secure Stripe page. You come straight back to your dashboard when it's done."],
      shots: [["store-register", "Fill in the form, tick the terms box, then Create account."],
              ["store-plan", "Choose a plan, tick the box, then Continue to secure checkout."]] },
    { h: "Pick your bots", path: "Dashboard  >  Step 2 - Pick your bots",
      do: ["Tick the bot(s) you want. Each one shows the symbol and timeframe it trades.",
           "Click Save picks. Make a note of the symbol and timeframe shown for each bot you picked - you'll need them in step 8."],
      shots: [["store-picks", "Tick a bot, then click Save picks."]] },
    { h: "Install MetaTrader 5 and log in", path: "MT5  >  File  >  Login to Trade Account",
      do: ["Download MetaTrader 5 (MT5) from your broker's website and install it like any normal program.",
           "Open MT5, click File at the top left, then Login to Trade Account.",
           "Type the account number, password and server your broker emailed you, then click OK.",
           "Start with a DEMO account (free practice money) - your broker can give you one in a minute."],
      shots: [["mt5-file-login", "File menu > Login to Trade Account."]],
      tip: "When you're logged in you'll see prices moving in the Market Watch list on the left." },
    { h: "Allow BotGenomix to check your licence", path: "MT5  >  Tools  >  Options  >  Expert Advisors", warn: true,
      do: ["Click Tools at the top, then Options.", "Open the Expert Advisors tab.",
           "Tick the box Allow WebRequest for listed URL.",
           "Click the empty line underneath (it says 'add new URL'), type https://botgenomix.com exactly, press Enter, then click OK."],
      shots: [["mt5-tools-options", "Tools menu > Options."],
              ["mt5-options-webrequest", "Expert Advisors tab > tick Allow WebRequest > add https://botgenomix.com > OK."]],
      tip: "This is the step most people miss. Without it the bot shows a red LICENCE REFUSED / cannot reach banner and won't trade." },
    { h: "Download your bot", path: "botgenomix.com  >  My account  >  Step 3 - Download + set up",
      do: ["On your dashboard, find your purchased bot under Download + set up.", "Click Download .ex5 - the file saves to your Downloads folder.",
           "Don't rename it and don't try to open it - MT5 will use it.",
           "Your licence key is shown just above - you'll copy it in step 10."],
      shots: [["store-mybots", "Click Download .ex5 next to your purchased bot."]] },
    { h: "Open MT5's bot folder", path: "MT5  >  File  >  Open Data Folder  >  MQL5  >  Experts",
      do: ["In MT5 click File, then Open Data Folder. A normal Windows folder window opens.",
           "Double-click the MQL5 folder.", "Double-click the Experts folder.", "Keep this window open for the next step."],
      shots: [["mt5-file-datafolder", "File menu > Open Data Folder."],
              ["mt5-datafolder-mql5", "In the window that opens, double-click MQL5."],
              ["mt5-mql5-experts", "Then double-click Experts."]] },
    { h: "Copy the bot in and refresh", path: "MQL5  >  Experts, then MT5 Navigator",
      do: ["Open a second Windows folder window (press the Windows key + E) and go to your Downloads folder - that's where your browser saved the bot's .ex5 file.",
           "Place the two windows side by side: Downloads on one side, the MT5 Experts folder (from step 6) on the other.",
           "Click and hold the .ex5 file in Downloads, drag it across into the Experts window, then let go. (Or right-click it > Copy, then right-click inside Experts > Paste.)",
           "Go back to MT5. If you can't see the Navigator panel, press Ctrl+N.",
           "Right-click Expert Advisors in the Navigator and click Refresh. Your bot now appears in the list."],
      shots: [["mt5-experts-folder", "This is the Experts folder - the path at the top says MQL5 > Experts. Drag your .ex5 file from Downloads into this list."],
              ["mt5-navigator", "After Refresh your bot appears in the Navigator."]] },
    { h: "Open the right chart", path: "Market Watch  >  Symbol  >  Chart Window",
      do: ["In Market Watch (Ctrl+M) find the symbol your bot was built for - it's shown next to your bot on your dashboard. Some brokers add a letter or suffix to the symbol name - that's fine.",
           "Right-click it and choose Chart Window.",
           "Set the chart to the timeframe your bot was backtested on (also shown next to your bot) using the timeframe buttons at the top of MT5."],
      shots: [["mt5-workspace", "Find your bot's symbol in Market Watch, open its chart, then click your bot's timeframe button."]],
      tip: "Every bot only works on the symbol + timeframe it was backtested on. Wrong chart = wrong results." },
    { h: "Attach the bot to the chart", path: "Navigator  >  Expert Advisors",
      do: ["In the Navigator, find your purchased bot under Expert Advisors.", "Click and hold it, drag it onto the chart, then let go.", "A settings window opens automatically."],
      shots: [["mt5-navigator", "Click and hold your bot in the Navigator, drag it onto the chart, then let go."]] },
    { h: "Paste your licence key", path: "Expert Advisor settings  >  Inputs",
      do: ["On your dashboard click Copy next to your licence key.", "Back in MT5's settings window click the Inputs tab.",
           "Double-click the Value box next to BotGenomix licence key and paste your key.",
           "Leave every other setting as it is, then click OK."],
      shots: [["store-mybots", "Click Copy next to Your licence key."],
              ["mt5-inputs-licence", "Inputs tab > paste your key into the first row > OK."]],
      tip: "Copy and paste rather than typing - one wrong character and the licence is refused." },
    { h: "Turn on Algo Trading", path: "MT5 toolbar  >  Algo Trading",
      do: ["In the settings window's Common tab make sure Allow Algo Trading is ticked.",
           "On the MT5 toolbar click the Algo Trading button so it turns green.",
           "A small hat icon in the top-right of the chart turns blue - that means the bot is switched on."],
      shots: [["mt5-algo-trading", "Click Algo Trading on the toolbar so it turns green."]] },
    { h: "Check the licence banner", path: "Your chart, top-left corner", final: true,
      do: ["As soon as you attach the bot a blue CHECKING LICENCE banner appears top-left, then turns into a colour within a few seconds.",
           "Green LICENCE OK = all done, the bot is trading.",
           "Leave MT5 open - the bot only trades while MT5 is running (many people use a cheap VPS for this)."],
      shots: [["mt5-chart-banner", "Big green LICENCE OK banner top-left and a blue hat top-right = your bot is licensed and trading."]] },
  ];
}

const TROUBLE = [
  ["Red banner: 'cannot reach botgenomix.com'", "Step 4 wasn't done. Add https://botgenomix.com under Tools > Options > Expert Advisors, then remove and re-attach the bot."],
  ["Red banner: 'unknown licence key'", "Paste the key again from your dashboard - no spaces before or after."],
  ["Red banner: 'not in your picks'", "Tick that bot under Pick your bots on your dashboard and save."],
  ["Red banner: 'already linked to a demo/live account'", "Each bot runs on 1 demo + 1 live account. Unlink the old account on your dashboard, then re-attach."],
  ["Red banner: 'subscription not active'", "Your plan has ended or a payment failed - check Manage billing on your dashboard."],
  ["No banner and the hat is grey", "Algo Trading is off - click the Algo Trading button on the toolbar (step 11)."],
];

const DATA_SENT = [
  "Licence check (when the bot starts and every few hours): your licence key, the bot's product code, your MT5 account number, your broker's server name and whether it's a demo or real account.",
  "Performance report (at most once a minute, only when something has changed): account currency, balance and equity, this bot's open profit/loss, and your account's closed trades and balance operations (ticket, time, symbol, magic number, profit, swap and commission).",
  "We use this only to check your licence and to show results on your own dashboard. It isn't shown to anyone else.",
];

export default function SetupGuide() {
  const nav = useNavigate();
  const [, setMe] = useState(null);
  useEffect(() => {
    if (!hasCustomerToken()) { nav("/account/login?next=/account/setup", { replace: true }); return; }
    customerApi.get("/store/account/me").then((r) => setMe(r.data)).catch(() => nav("/account/login?next=/account/setup"));
  }, [nav]);
  const steps = buildSteps();
  return (
    <div data-testid="setup-guide-page" className="min-h-screen bg-[#050914] text-[#F1F5F9]">
      <StoreTopbar subtitle="Setup guide" />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/account" className="text-xs uppercase tracking-widest text-[#94A3B8] hover:text-white" data-testid="guide-back">← Dashboard</Link>
        <h1 className="text-4xl sm:text-5xl font-medium tracking-tight mt-4">Set up your bot in 12 steps</h1>
        <p className="text-base text-[#94A3B8] mt-4 leading-relaxed">
          No coding experience needed. Takes about 15 minutes. Follow the steps in order, don't skip any, and look for
          the <b className="text-[#34D399]">green boxes</b> in each picture.
        </p>
        <nav className="mt-6 flex flex-wrap gap-2">
          {steps.map((s, i) => (
            <a key={i} href={`#step-${i + 1}`} data-testid={`guide-jump-${i + 1}`} className={`text-xs px-3 py-1.5 rounded-full border ${s.warn ? "border-[#FBBF24] text-[#FBBF24]" : "border-blue-900/60 text-[#94A3B8]"} hover:text-white`}>
              {i + 1}. {s.h}
            </a>
          ))}
        </nav>
        {steps.map((s, i) => (
          <section key={i} id={`step-${i + 1}`} data-testid={`guide-step-${i + 1}`} className="mt-10 rounded-2xl border border-[#1E293B] bg-[#0B1220] p-6 sm:p-8">
            <div className="text-xs font-bold tracking-[0.25em] text-[#34D399]">STEP {String(i + 1).padStart(2, "0")}</div>
            <h2 className="text-2xl sm:text-3xl font-semibold mt-2">{s.h}</h2>
            <div className="text-sm text-[#94A3B8] mt-1">{s.path}</div>
            {s.warn && (
              <div className="mt-4 flex gap-3 rounded-xl border-2 border-[#FBBF24] bg-[#FBBF24]/10 p-4 text-[#FDE68A]" data-testid="guide-webrequest-warning">
                <WarningIcon size={22} weight="fill" className="flex-shrink-0 text-[#FBBF24]" />
                <div className="text-sm"><b>Don't skip this step.</b> The bot checks your licence over the internet. MT5 blocks that unless you add our address here.</div>
              </div>
            )}
            <ol className="mt-5 space-y-2.5">
              {s.do.map((d, j) => (
                <li key={j} className="flex gap-3 text-base text-[#E2E8F0] leading-relaxed">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1E3A8A] text-xs flex items-center justify-center mt-0.5">{j + 1}</span>
                  <span>{d}</span>
                </li>
              ))}
            </ol>
            {s.shots?.map(([src, cap], k) => <Shot key={src} id={`${i + 1}-${k + 1}`} src={src} cap={cap} />)}
            {s.final && <ChartBanner botName="Your purchased bot" />}
            {s.tip && (
              <div className="mt-5 flex gap-3 rounded-xl bg-[#38BDF8]/10 border border-[#38BDF8]/30 p-4 text-sm text-[#BAE6FD]">
                <LightbulbIcon size={20} weight="fill" className="flex-shrink-0 text-[#38BDF8]" /> <span>{s.tip}</span>
              </div>
            )}
          </section>
        ))}
        <section className="mt-12 rounded-2xl border border-[#1E293B] bg-[#0B1220] p-6" data-testid="guide-data-sent">
          <h2 className="text-2xl font-semibold">What the bot sends to BotGenomix</h2>
          <ul className="mt-4 space-y-2 text-sm text-[#CBD5E1] list-disc pl-5">
            {DATA_SENT.map((d) => <li key={d}>{d}</li>)}
          </ul>
          <p className="mt-4 text-sm text-[#94A3B8]">It never sends your MT5 password, investor password or any payment details. The licence check only receives a yes/no answer - it can't change the bot's strategy or settings, or send trading instructions. If the licence check fails or your plan ends, the bot pauses and stops opening new trades.</p>
        </section>
        <RiskNotice testid="guide-risk" className="mt-8" />
        <section className="mt-12" data-testid="guide-troubleshooting">
          <h2 className="text-2xl font-semibold">Something not right?</h2>
          <div className="mt-4 space-y-3">
            {TROUBLE.map(([q, a]) => (
              <div key={q} className="rounded-xl border border-[#1E293B] bg-[#0B1220] p-4">
                <div className="font-semibold text-[#FCA5A5]">{q}</div>
                <div className="text-sm text-[#CBD5E1] mt-1">{a}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-2 text-sm text-[#94A3B8]">
            <CheckCircleIcon size={18} className="text-[#34D399]" /> Still stuck? Email botgenomix.support@gmail.com with a screenshot of your chart.
          </div>
        </section>
      </main>
      <HubFooter />
    </div>
  );
}
